/**
 * ServicesPage Component (Web Version)
 * Maintains identical business logic and design from Mobile-client-core ServicesScreen.tsx
 * Features: Swipeable cards, expandable details, image carousels, gesture interactions
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigation } from '../shared/hooks/useNavigation';
import { ROUTES } from '../shared/navigation';
import { 
  ArrowLeft, 
  ShoppingCart, 
  Plus, 
  Trash2, 
  Eye,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

// Unused imports for linting
const _ChevronDown = ChevronDown;
const _ChevronUp = ChevronUp;
import BottomNavigation from '../components/BottomNavigation';
import useServicesStore from '../stores/servicesStore';
import { buildAssetUrl } from '../utils/urlHelpers';
import { buildServiceImages } from '../utils/imageUtils';
import SchedulingWizardModal from '../components/SchedulingWizardModal';
import { ServiceVariations } from '../components/VariationChip';
import { getServiceVariations as fetchServiceVariationsFromAPI } from '../services/serviceVariationsService';

const ServicesPage = () => {
  const { navigate } = useNavigation();
  const { 
    selectedServices, 
    removeService, 
    clearServices: _clearServices, 
    getTotalPrice, 
    getTotalDuration: _getTotalDuration,
    setServiceVariation,
    toggleServiceVariation,
    getServiceVariations,
    getServiceFinalPrice,
    getServiceFinalDuration,
    getExpandedServicesCount
  } = useServicesStore();
  
  // State for expandable cards (identical to mobile pattern)
  const [expandedServiceIds, setExpandedServiceIds] = useState(new Set());
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [selectedImageForModal, setSelectedImageForModal] = useState(null);
  
  // State for scheduling wizard modal
  const [wizardModalVisible, setWizardModalVisible] = useState(false);
  
  // Service variations state
  const [serviceVariations, setServiceVariations] = useState({}); // { serviceId: [variationGroups] }

  // Helper functions (identical to mobile)
  const formatDuration = (minutes) => {
    if (minutes === undefined || minutes === null || minutes <= 0) return '-';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h > 0 ? h + 'h ' : ''}${m > 0 ? m + 'min' : ''}`.trim() || '-';
  };

  const formatPrice = (priceStr) => {
    const priceNum = typeof priceStr === 'number' ? priceStr : parseFloat(priceStr);
    if (isNaN(priceNum)) return 'R$ -';
    return `R$ ${priceNum.toFixed(2).replace('.', ',')}`;
  };

  const _getFullImageUrl = (relativePath) => {
    return buildAssetUrl(relativePath);
  };

  // buildServiceImages function now imported from imageUtils utility

  // Service card expansion handlers (identical to mobile pattern)
  const handleToggleServiceExpansion = (serviceId) => {
    setExpandedServiceIds(prev => {
      const newSet = new Set();
      // If the clicked service is already expanded, close it (empty set)
      // Otherwise, expand only the clicked service
      if (!prev.has(serviceId)) {
        newSet.add(serviceId);
      }
      return newSet;
    });
  };

  const isServiceExpanded = (serviceId) => {
    return expandedServiceIds.has(serviceId);
  };

  // Handle image modal
  const handleImagePress = (imageUrl) => {
    setSelectedImageForModal(imageUrl);
    setImageModalVisible(true);
  };

  // Handle variation selection
  const handleVariationSelect = (serviceId, groupId, variation) => {
    setServiceVariation(serviceId, groupId, variation);
  };

  const handleVariationToggle = (serviceId, groupId, variation) => {
    toggleServiceVariation(serviceId, groupId, variation);
  };

  // Load variations for a service from API
  const loadServiceVariationsFromAPI = async (serviceId) => {
    try {
      const variations = await fetchServiceVariationsFromAPI(serviceId);
      setServiceVariations(prev => ({
        ...prev,
        [serviceId]: variations
      }));
    } catch (err) {
      // Silently fail - variations are optional
      setServiceVariations(prev => ({
        ...prev,
        [serviceId]: []
      }));
    }
  };

  // Load variations for selected services (for display in variation picker)
  useEffect(() => {
    selectedServices.forEach(service => {
      // Use originalServiceId for cart items, otherwise use id
      const serviceId = service.originalServiceId || service.id;
      if (!serviceVariations[serviceId]) {
        loadServiceVariationsFromAPI(serviceId);
      }
    });
  }, [selectedServices]);

  return (
    <div className="h-full flex flex-col bg-brand-light dark:bg-brand-dark">
      <div className="flex-1 overflow-hidden">
        {/* Fixed Header - Luxury Design */}
        <div className="fixed top-0 left-0 right-0 z-40 bg-brand-dark dark:bg-brand-light border-b border-brand-light/10 dark:border-brand-dark/10">
          <div className="safe-area-top px-6 py-6 flex items-center">
            <button
              onClick={() => navigate(ROUTES.DASHBOARD)}
              className="mr-4 p-2 border border-brand-light/20 dark:border-brand-dark/20 hover:bg-brand-light/10 dark:hover:bg-brand-dark/10 transition-all duration-300"
            >
              <ArrowLeft size={20} className="text-brand-light dark:text-brand-dark" strokeWidth={1.5} />
            </button>
            <h1 className="text-xl font-serif font-bold text-brand-light dark:text-brand-dark flex-1 tracking-wide">
              Carrinho
            </h1>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="h-full overflow-y-auto mobile-scroll" style={{ paddingTop: '72px', paddingBottom: '160px' }}>
          {/* Main Content */}
          <div className="bg-brand-light dark:bg-brand-dark px-6 py-4 min-h-0">
            {selectedServices.length === 0 ? (
              // Empty State - Luxury Design
              <div className="text-center py-20 border border-brand-dark/10 dark:border-brand-light/10">
                <ShoppingCart size={56} className="text-brand-dark/20 dark:text-brand-light/20 mx-auto mb-6" strokeWidth={1.5} />
                <h3 className="text-xl font-serif font-bold text-brand-dark dark:text-brand-light mb-3 tracking-wide">
                  Carrinho vazio
                </h3>
                <p className="text-brand-dark/60 dark:text-brand-light/60 mb-8 tracking-wide">
                  Adicione serviços para continuar
                </p>

                <button
                  onClick={() => navigate(ROUTES.DASHBOARD)}
                  className="inline-flex items-center space-x-2 px-8 py-4 bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border border-brand-dark dark:border-brand-light font-medium text-sm tracking-widest uppercase hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 transition-all duration-300"
                >
                  <Plus size={18} strokeWidth={1.5} />
                  <span>Adicionar Serviços</span>
                </button>
              </div>
            ) : (
              // Service Cards List
              <div className="space-y-4">
                {selectedServices.map((service) => {
                  // Use cartItemId if available (for services with variations), otherwise use id
                  const itemId = service.cartItemId || service.id;
                  const isExpanded = isServiceExpanded(itemId);
                  const serviceImages = buildServiceImages(service);
                  // For cart items with embedded variation, don't show variation picker
                  const hasEmbeddedVariation = !!service.selectedVariation;
                  const variations = hasEmbeddedVariation ? [] : (serviceVariations[service.originalServiceId || service.id] || []);
                  const selectedVariationsFromStore = hasEmbeddedVariation ? {} : getServiceVariations(service.id);
                  // Use embedded price/duration for items with variations
                  const finalPrice = hasEmbeddedVariation ? service.price : getServiceFinalPrice(service);
                  const finalDuration = hasEmbeddedVariation ? service.duration_minutes : getServiceFinalDuration(service);

                  return (
                    <SwipeableServiceCard
                      key={itemId}
                      service={service}
                      isExpanded={isExpanded}
                      onToggleExpanded={(id) => handleToggleServiceExpansion(itemId)}
                      onRemove={() => removeService(itemId)}
                      onImagePress={handleImagePress}
                      serviceImages={serviceImages}
                      formatDuration={formatDuration}
                      formatPrice={formatPrice}
                      variations={variations}
                      selectedVariations={selectedVariationsFromStore}
                      onVariationSelect={handleVariationSelect}
                      finalPrice={finalPrice}
                      finalDuration={finalDuration}
                      embeddedVariation={service.selectedVariation}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Action Bar - Luxury Design */}
      {selectedServices.length > 0 && (
        <div className="fixed bottom-16 left-0 right-0 bg-brand-light dark:bg-brand-dark border-t border-brand-dark/10 dark:border-brand-light/10 p-6 z-50">
          {/* Total Price Display */}
          <div className="flex justify-between items-center mb-5 pb-4 border-b border-brand-dark/10 dark:border-brand-light/10">
            <span className="text-brand-dark/60 dark:text-brand-light/60 uppercase tracking-wider text-xs">
              Total ({getExpandedServicesCount()} {getExpandedServicesCount() === 1 ? 'serviço' : 'serviços'}):
            </span>
            <span className="text-2xl font-serif font-bold text-brand-dark dark:text-brand-light">
              {formatPrice(getTotalPrice())}
            </span>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={() => navigate(ROUTES.DASHBOARD)}
              className="flex-1 flex items-center justify-center space-x-1 py-4 px-4 border border-brand-dark/20 dark:border-brand-light/20 text-brand-dark dark:text-brand-light font-medium text-xs uppercase tracking-wide hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300"
            >
              <Plus size={16} strokeWidth={1.5} />
              <span>Serviços</span>
            </button>

            <button
              onClick={() => {
                // Open scheduling wizard modal
                setWizardModalVisible(true);
              }}
              className="flex-[2] py-4 px-6 bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border border-brand-dark dark:border-brand-light font-medium uppercase tracking-wide text-sm hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 transition-all duration-300"
            >
              Escolher data
            </button>
          </div>
        </div>
      )}

      {/* Image Modal - Luxury Design */}
      {imageModalVisible && (
        <div className="fixed inset-0 bg-brand-dark/95 dark:bg-black/95 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setImageModalVisible(false)}>
          <div className="relative max-w-2xl max-h-[80vh] m-4">
            <button
              onClick={() => setImageModalVisible(false)}
              className="absolute -top-12 right-0 p-3 text-brand-light hover:bg-brand-light/10 transition-all duration-300"
            >
              <X size={24} strokeWidth={1.5} />
            </button>
            <img
              src={selectedImageForModal}
              alt="Service"
              className="w-full h-full object-contain"
            />
          </div>
        </div>
      )}

      {/* Scheduling Wizard Modal */}
      {wizardModalVisible && (
        <SchedulingWizardModal
          isVisible={wizardModalVisible}
          onClose={() => setWizardModalVisible(false)}
          selectedServices={selectedServices.map(service => {
            // For cart items with embedded variation (created by addServiceWithVariation),
            // use the embedded data directly
            if (service.selectedVariation && service.cartItemId) {
              return {
                ...service,
                // price and duration_minutes are already calculated in the cart item
                selectedVariations: { [service.selectedVariation.groupId]: service.selectedVariation },
                variation_id: service.selectedVariation.id
              };
            }

            // For legacy items, build variation data from store lookup
            const variations = getServiceVariations(service.id);
            const variationEntries = Object.entries(variations || {});

            // Build selectedVariation object from first non-array variation
            let selectedVariation = null;
            let variation_id = null;

            for (const [groupId, selection] of variationEntries) {
              if (selection && !Array.isArray(selection) && selection.name) {
                selectedVariation = {
                  id: selection.id,
                  name: selection.name,
                  groupId: groupId,
                  price_delta: selection.price_delta || 0,
                  duration_delta: selection.duration_delta || 0
                };
                variation_id = selection.id;
                break;
              } else if (Array.isArray(selection) && selection.length > 0 && selection[0].name) {
                selectedVariation = {
                  id: selection[0].id,
                  name: selection.map(v => v.name).join(', '),
                  groupId: groupId,
                  price_delta: selection.reduce((sum, v) => sum + (v.price_delta || 0), 0),
                  duration_delta: selection.reduce((sum, v) => sum + (v.duration_delta || 0), 0)
                };
                variation_id = selection[0].id;
                break;
              }
            }

            return {
              ...service,
              price: getServiceFinalPrice(service),
              duration_minutes: getServiceFinalDuration(service),
              selectedVariation: selectedVariation || service.selectedVariation, // Fallback to embedded
              selectedVariations: variations,
              variation_id: variation_id || service.variation_id // Fallback to embedded
            };
          })}
        />
      )}

      <BottomNavigation />
    </div>
  );
};

// SwipeableServiceCard Component (identical to mobile functionality)
const SwipeableServiceCard = ({
  service,
  isExpanded,
  onToggleExpanded,
  onRemove,
  onImagePress,
  serviceImages,
  formatDuration,
  formatPrice,
  variations = [],
  selectedVariations = {},
  onVariationSelect,
  finalPrice,
  finalDuration,
  embeddedVariation = null
}) => {
  const [isRemoving, setIsRemoving] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const cardRef = useRef(null);
  const startX = useRef(0);
  const currentX = useRef(0);

  // Check if service has price subject to evaluation
  const serviceSubjectToEvaluation = service.price_subject_to_evaluation || false;

  // Touch/Mouse handlers for swipe gesture (web adaptation of mobile swipe)
  const handleStart = (clientX) => {
    startX.current = clientX;
    currentX.current = clientX;
  };

  const handleMove = (clientX) => {
    if (startX.current === 0) return;
    
    currentX.current = clientX;
    const diff = clientX - startX.current;
    
    // Only allow left swipe (negative values)
    if (diff < 0) {
      setSwipeOffset(Math.max(diff, -120));
    }
  };

  const handleEnd = () => {
    const diff = currentX.current - startX.current;
    
    if (diff < -80) {
      // Trigger removal
      setIsRemoving(true);
      setTimeout(() => {
        onRemove(service.id);
      }, 200);
    } else {
      // Snap back
      setSwipeOffset(0);
    }
    
    startX.current = 0;
    currentX.current = 0;
  };

  return (
    <div className="relative overflow-hidden border border-brand-dark/10 dark:border-brand-light/10 bg-brand-light dark:bg-brand-dark">
      {/* Red background for swipe-to-delete */}
      <div className="absolute inset-0 bg-red-600 dark:bg-red-400 flex items-center justify-end pr-6">
        <div className="flex items-center space-x-2 text-white dark:text-black">
          <Trash2 size={20} strokeWidth={1.5} />
          <span className="font-medium uppercase tracking-wider text-sm">Remover</span>
        </div>
      </div>

      {/* Main card content */}
      <div
        ref={cardRef}
        className={`relative bg-brand-light dark:bg-brand-dark transition-transform duration-200 ${isRemoving ? 'opacity-0 -translate-x-full' : ''}`}
        style={{ transform: `translateX(${swipeOffset}px)` }}
        onTouchStart={(e) => handleStart(e.touches[0].clientX)}
        onTouchMove={(e) => handleMove(e.touches[0].clientX)}
        onTouchEnd={handleEnd}
        onMouseDown={(e) => handleStart(e.clientX)}
        onMouseMove={(e) => e.buttons === 1 && handleMove(e.clientX)}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
      >
        {/* Service Card Header - Always Visible */}
        <div className="p-5 border-b border-brand-dark/10 dark:border-brand-light/10">
          <div className="flex items-start justify-between">
            <div
              className="flex-1 cursor-pointer"
              onClick={() => onToggleExpanded(service.id)}
            >
              <h3 className="text-lg font-serif font-bold text-brand-dark dark:text-brand-light mb-2 tracking-wide">
                {service.name}
              </h3>
              {/* Show embedded variation name if any (for cart items added with variation) */}
              {embeddedVariation && embeddedVariation.name && embeddedVariation.name !== 'Padrão' && (
                <span className="inline-block text-xs bg-brand-dark/10 dark:bg-brand-light/10 text-brand-dark dark:text-brand-light border border-brand-dark/30 dark:border-brand-light/30 px-2 py-1 mb-2 tracking-wide">
                  {embeddedVariation.name}
                </span>
              )}
              {/* Fallback: Show selected variation from store if no embedded variation */}
              {!embeddedVariation && (() => {
                const variationEntries = Object.entries(selectedVariations || {});
                for (const [groupId, selection] of variationEntries) {
                  if (selection && !Array.isArray(selection) && selection.name) {
                    return (
                      <span className="inline-block text-xs bg-brand-dark/10 dark:bg-brand-light/10 text-brand-dark dark:text-brand-light border border-brand-dark/30 dark:border-brand-light/30 px-2 py-1 mb-2 tracking-wide">
                        {selection.name}
                      </span>
                    );
                  } else if (Array.isArray(selection) && selection.length > 0 && selection[0].name) {
                    return (
                      <span className="inline-block text-xs bg-brand-dark/10 dark:bg-brand-light/10 text-brand-dark dark:text-brand-light border border-brand-dark/30 dark:border-brand-light/30 px-2 py-1 mb-2 tracking-wide">
                        {selection.map(v => v.name).join(', ')}
                      </span>
                    );
                  }
                }
                return null;
              })()}

              {/* Service Variations - Show below service name */}
              {variations.length > 0 && (
                <div className="mb-3">
                  <ServiceVariations
                    variationGroups={variations}
                    selectedVariations={selectedVariations}
                    onVariationSelect={(groupId, variation) => handleVariationSelect(service.id, groupId, variation)}
                    onVariationToggle={(groupId, variation) => handleVariationToggle(service.id, groupId, variation)}
                    size="small"
                    basePrice={parseFloat(service.price)}
                    baseDuration={service.duration_minutes || 0}
                    serviceSubjectToEvaluation={serviceSubjectToEvaluation}
                  />
                </div>
              )}


              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-brand-dark dark:text-brand-light font-serif font-bold text-base">
                    {formatPrice(finalPrice || service.price)}
                  </span>
                  <span className="text-brand-dark/50 dark:text-brand-light/50 text-xs uppercase tracking-wider">
                    {formatDuration(finalDuration || service.duration_minutes)}
                  </span>
                </div>

                {/* Expand hint */}
                <div className="flex items-center space-x-1 text-brand-dark/40 dark:text-brand-light/40">
                  <Eye size={14} strokeWidth={1.5} />
                  <span className="text-[10px] uppercase tracking-wider">Detalhes</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => onRemove(service.id)}
              className="p-2 text-red-600 dark:text-red-400 border border-red-600/20 dark:border-red-400/20 hover:bg-red-600 hover:dark:bg-red-400 hover:text-white hover:dark:text-black transition-all duration-300 ml-3"
            >
              <Trash2 size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Expandable Content */}
        {isExpanded && (
          <div className="border-t border-brand-dark/10 dark:border-brand-light/10 p-5">
            {/* Service Images Carousel */}
            {serviceImages.length > 0 && (
              <div className="mb-5">
                <div className="flex space-x-3 overflow-x-auto pb-2 scrollbar-hide">
                  {serviceImages.map((image, index) => (
                    <div key={index} className="flex-shrink-0">
                      <img
                        src={image.src}
                        alt={image.caption}
                        className="w-28 h-28 object-cover cursor-pointer hover:opacity-90 transition-opacity duration-300"
                        onClick={() => onImagePress(image.src)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Service Description */}
            {service.description && (
              <div>
                <h4 className="text-xs font-medium text-brand-dark/50 dark:text-brand-light/50 uppercase tracking-wider mb-3">Descrição</h4>
                <div
                  className="text-brand-dark dark:text-brand-light text-sm prose prose-sm max-w-none leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: service.description }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicesPage;